import React from "react";
import {Row, Col, UncontrolledTooltip} from 'reactstrap';
import {formatCredits} from '../../utils/credits';
import {countryName} from '../../utils/countries';
import MetaChip from '../MetaChip';
import { getSystemStatus, formatSyncAge, SystemStatusDetails, SYNC_INTERVAL_MS } from '../StatusIcon';

import './OrganizationCard.scss';
import TierBadge from "../TierBadge";
import Avatar from '../Avatar';

// The three systems the on-site cronjob reports on, in the order the status
// legend lists them.
const SYSTEMS = [
    { key: 'mediablock', icon: 'fa-cube' },
    { key: 'bacServer', icon: 'fa-server' },
    { key: 'tms', icon: 'fa-gears' },
];

const OrganizationCard = ({ organization }) => {
    const status = getSystemStatus(organization);

    // A site that hasn't reported within the cronjob's interval is flagged: the
    // statuses above it are last-known values, not live ones.
    const syncedAt = organization.lastSynced ? new Date(organization.lastSynced).getTime() : null;
    const syncAge = syncedAt ? Date.now() - syncedAt : null;
    const syncStale = syncAge === null || syncAge > SYNC_INTERVAL_MS;

    // Tooltips are portalled to the body, so their targets need ids of their own.
    const systemsId = `systems-${organization._id}`;
    const syncId = `synced-${organization._id}`;

    return (
        <Row className="organization-card-component align-items-center py-3 px-2 mx-0 my-2">
            <Col xs="auto">
                <Avatar imageUrl={organization.logo?.imageUrl} name={organization.name} size={64} />
            </Col>

            <Col>
                <div className="d-flex align-items-center gap-3">
                    <div className="name text-uppercase text-primary">
                        {organization.name}
                    </div>

                    {/* The live systems sit with the name: they say whether the
                        site is up, the band below says what it is. */}
                    <span className="system-status d-inline-flex align-items-center gap-2" id={systemsId}>
                        {SYSTEMS.map(({ key, icon }) => (
                            <span key={key} className={`fas ${icon} icons-${status[key] ? 'on' : 'off'}`}/>
                        ))}
                    </span>
                    <UncontrolledTooltip target={systemsId} placement="bottom" className="detail-tooltip" autohide={false}>
                        <SystemStatusDetails organization={organization}/>
                    </UncontrolledTooltip>
                </div>

                {/* One band of equal-height pills: the balance, sync freshness,
                    then where the organization sits and where its content is
                    served from. */}
                <div className="meta-band d-flex flex-wrap align-items-center gap-2 mt-2">
                    <MetaChip icon="fa-coins" label="Credits">
                        <span className="credits-value">{formatCredits(organization.creditAmount)}</span>
                    </MetaChip>

                    {/* The chip keeps the age short; the tooltip carries the
                        timestamp and the precise figure. */}
                    <MetaChip
                        id={syncId}
                        icon={syncStale ? 'fa-triangle-exclamation' : 'fa-rotate'}
                        label="Synced"
                        className={syncStale ? 'text-warning' : ''}
                    >
                        {syncAge === null ? 'never' : `${formatSyncAge(syncAge, { compact: true })} ago`}
                    </MetaChip>
                    <UncontrolledTooltip target={syncId} placement="bottom" className="detail-tooltip">
                        {syncedAt ? (
                            <div className="text-start p-1">
                                <div className="fw-bold border-bottom pb-1 mb-2">
                                    <span className="fas fa-rotate me-2"/>
                                    Last sync
                                </div>
                                <div>{new Date(syncedAt).toLocaleString()}</div>
                                <div className="text-white-50">{formatSyncAge(syncAge)} ago</div>
                            </div>
                        ) : 'This site has never reported in'}
                    </UncontrolledTooltip>

                    {organization.country && (
                        <MetaChip icon="fa-map-marker-alt" label="Territory">
                            {countryName(organization.country)}
                        </MetaChip>
                    )}

                    {organization.siteLocation && (
                        <MetaChip icon="fa-globe" label="Location">
                            {countryName(organization.siteLocation)}
                        </MetaChip>
                    )}

                    {organization.isYacht && <MetaChip icon="fa-ship" label="Yacht"/>}
                </div>
            </Col>

            <Col xs="auto" className="d-flex align-items-center gap-3">
                <TierBadge tier={organization.tier}/>
                <span className="go-arrow fas fa-chevron-right"/>
            </Col>
        </Row>
    );
};

export default OrganizationCard;
